import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const StockOverviewTable = ({ products, onProductSelect, onQuickAdjust }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedProducts, setSelectedProducts] = useState([]);

  const getStockStatus = (current, minimum) => {
    if (current === 0) return { status: 'out-of-stock', color: 'text-error', bg: 'bg-error-50' };
    if (current <= minimum) return { status: 'low-stock', color: 'text-warning', bg: 'bg-warning-50' };
    if (current <= minimum * 2) return { status: 'medium-stock', color: 'text-accent', bg: 'bg-accent-50' };
    return { status: 'good-stock', color: 'text-success', bg: 'bg-success-50' };
  };

  const sortedProducts = useMemo(() => {
    if (!sortConfig.key) return products;

    return [...products].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [products, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return 'ArrowUpDown';
    return sortConfig.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border bg-secondary-50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-heading-semibold text-text-primary">
            Resumen de Stock
          </h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-text-secondary">
              {selectedProducts.length} de {products.length} seleccionados
            </span>
            {selectedProducts.length > 0 && (
              <Button
                variant="primary"
                size="sm"
                iconName="Edit"
                onClick={() => onQuickAdjust(selectedProducts)}
              >
                Ajustar Seleccionados
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-secondary-50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedProducts.length === products.length}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-1 text-sm font-body-medium text-text-secondary hover:text-text-primary"
                >
                  <span>Producto</span>
                  <Icon name={getSortIcon('name')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('sku')}
                  className="flex items-center space-x-1 text-sm font-body-medium text-text-secondary hover:text-text-primary"
                >
                  <span>SKU</span>
                  <Icon name={getSortIcon('sku')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('currentStock')}
                  className="flex items-center space-x-1 text-sm font-body-medium text-text-secondary hover:text-text-primary"
                >
                  <span>Stock Actual</span>
                  <Icon name={getSortIcon('currentStock')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('minimumStock')}
                  className="flex items-center space-x-1 text-sm font-body-medium text-text-secondary hover:text-text-primary"
                >
                  <span>Stock Mínimo</span>
                  <Icon name={getSortIcon('minimumStock')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('location')}
                  className="flex items-center space-x-1 text-sm font-body-medium text-text-secondary hover:text-text-primary"
                >
                  <span>Ubicación</span>
                  <Icon name={getSortIcon('location')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-sm font-body-medium text-text-secondary">Acciones</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedProducts.map((product) => {
              const stockStatus = getStockStatus(product.currentStock, product.minimumStock);
              const isSelected = selectedProducts.includes(product.id);
              
              return (
                <tr 
                  key={product.id}
                  className={`hover:bg-secondary-50 transition-micro ${isSelected ? 'bg-primary-50' : ''}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelectProduct(product.id)}
                      className="rounded border-border"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-secondary-100">
                        <Image
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <div className="text-sm font-body-medium text-text-primary">
                          {product.name}
                        </div>
                        <div className="text-xs text-text-secondary">
                          {product.category}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-data text-text-primary">
                      {product.sku}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <span className={`text-sm font-body-medium ${stockStatus.color}`}>
                        {product.currentStock.toLocaleString('es-ES')}
                      </span>
                      <div className={`px-2 py-1 rounded-full text-xs font-body-medium ${stockStatus.bg} ${stockStatus.color}`}>
                        {stockStatus.status === 'out-of-stock' && 'Sin Stock'}
                        {stockStatus.status === 'low-stock' && 'Stock Bajo'}
                        {stockStatus.status === 'medium-stock' && 'Stock Medio'}
                        {stockStatus.status === 'good-stock' && 'Stock Bueno'}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-text-secondary">
                      {product.minimumStock.toLocaleString('es-ES')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-1">
                      <Icon name="MapPin" size={14} className="text-text-muted" />
                      <span className="text-sm text-text-secondary">
                        {product.location}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onQuickAdjust([product.id])}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                        onClick={() => onProductSelect(product)}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {products.length === 0 && (
        <div className="px-6 py-12 text-center">
          <Icon name="Package" size={48} className="text-text-muted mx-auto mb-4" />
          <h3 className="text-lg font-heading-medium text-text-primary mb-2">
            No hay productos
          </h3>
          <p className="text-text-secondary">
            No se encontraron productos que coincidan con los filtros aplicados.
          </p>
        </div>
      )}
    </div>
  );
};

export default StockOverviewTable;